import noop from 'lodash/noop';
import { fromCallback } from 'xstate';

import { CorrelatedLogger } from '@eluve/logger';

type Input = { logger: CorrelatedLogger };
type GenericEvent = { type: string };

export const keepScreenAwake = fromCallback<GenericEvent, Input>(
  ({ input }) => {
    const { logger } = input;
    if (!(navigator && 'wakeLock' in navigator)) {
      return noop;
    }

    let wakeLock: WakeLockSentinel | null = null;
    let requesting = false;
    let pendingRetry = false;
    let stopped = false;

    const requestWakeLock = async () => {
      // request('screen') throws NotAllowedError unless the page is visible.
      if (
        stopped ||
        wakeLock !== null ||
        document.visibilityState !== 'visible'
      ) {
        return;
      }

      // A request is already in flight; remember to retry once it settles, in
      // case it resolves without a held lock (e.g. rejected after the page
      // briefly hid mid-flight) so we don't drop this visible recovery.
      if (requesting) {
        pendingRetry = true;
        return;
      }

      requesting = true;
      try {
        const sentinel = await navigator.wakeLock.request('screen');

        if (stopped) {
          await sentinel.release();
          return;
        }

        wakeLock = sentinel;
        wakeLock.addEventListener('release', () => {
          if (wakeLock === sentinel) {
            wakeLock = null;
          }
        });
        logger('info', 'Screen wake lock acquired');
      } catch (error) {
        logger('warn', 'Failed to request screen wake lock', {
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        requesting = false;
        if (pendingRetry) {
          pendingRetry = false;
          void requestWakeLock();
        }
      }
    };

    const visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        void requestWakeLock();
      }
    };

    void requestWakeLock();
    document.addEventListener('visibilitychange', visibilityChangeHandler);

    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
      void wakeLock?.release();
      wakeLock = null;
    };
  },
);
